import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { calculateMacroPercentages, getMacroPercentageColor, formatPercentage } from '../../utils/macroCalculations';

interface MacroChartProps {
    proteinGrams: number;
    carbsGrams: number;
    fatsGrams: number;
    totalCalories: number;
}

export const MacroChart: React.FC<MacroChartProps> = ({
    proteinGrams,
    carbsGrams,
    fatsGrams,
    totalCalories
}) => {
    const { isDark } = useTheme();
    const percentages = calculateMacroPercentages(proteinGrams, carbsGrams, fatsGrams, totalCalories);
    const statusColor = isDark ? (percentages.isValid ? '#6ee7b7' : '#fda4af') : getMacroPercentageColor(percentages);

    return (
        <div style={{
            background: 'var(--page-surface)',
            borderRadius: '12px',
            padding: '16px',
            border: `2px solid ${statusColor}`,
            marginBottom: '16px'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0, fontSize: '14px', color: 'var(--page-text-primary)' }}>Distribuição de Macronutrientes</h4>
                <span style={{
                    fontSize: '12px',
                    color: statusColor,
                    fontWeight: 600,
                    background: `${statusColor}15`,
                    padding: '4px 8px',
                    borderRadius: '999px'
                }}>
                    {percentages.isValid ? '✓ Válido' : `Total: ${formatPercentage(percentages.total)}`}
                </span>
            </div>

            {/* Progress Bars */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Protein */}
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--page-text-muted)' }}>Proteínas</span>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: isDark ? '#7dd3fc' : '#0369a1' }}>
                            {formatPercentage(percentages.protein)} ({proteinGrams}g)
                        </span>
                    </div>
                    <div style={{
                        height: '8px',
                        background: 'var(--page-border)',
                        borderRadius: '999px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${Math.min(percentages.protein, 100)}%`,
                            height: '100%',
                            background: 'linear-gradient(90deg, #0ea5e9, #3b82f6)',
                            transition: 'width 0.3s ease'
                        }} />
                    </div>
                </div>

                {/* Carbs */}
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--page-text-muted)' }}>Carboidratos</span>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: isDark ? '#6ee7b7' : '#047857' }}>
                            {formatPercentage(percentages.carbs)} ({carbsGrams}g)
                        </span>
                    </div>
                    <div style={{
                        height: '8px',
                        background: 'var(--page-border)',
                        borderRadius: '999px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${Math.min(percentages.carbs, 100)}%`,
                            height: '100%',
                            background: 'linear-gradient(90deg, #10b981, #059669)',
                            transition: 'width 0.3s ease'
                        }} />
                    </div>
                </div>

                {/* Fats */}
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--page-text-muted)' }}>Gorduras</span>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: isDark ? '#fcd34d' : '#92400e' }}>
                            {formatPercentage(percentages.fats)} ({fatsGrams}g)
                        </span>
                    </div>
                    <div style={{
                        height: '8px',
                        background: 'var(--page-border)',
                        borderRadius: '999px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${Math.min(percentages.fats, 100)}%`,
                            height: '100%',
                            background: 'linear-gradient(90deg, #f59e0b, #d97706)',
                            transition: 'width 0.3s ease'
                        }} />
                    </div>
                </div>
            </div>

            {/* Validation Message with Suggestions */}
            {!percentages.isValid && totalCalories > 0 && (
                <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: isDark ? '#422006' : '#fef3c7',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: isDark ? '#fde68a' : '#92400e'
                }}>
                    <div style={{ fontWeight: 600, marginBottom: '6px' }}>⚠️ Distribuição não está em 100%</div>
                    <div style={{ marginBottom: '8px' }}>
                        Total atual: {formatPercentage(percentages.total)}
                    </div>
                    {Math.abs(percentages.total - 100) > 1 && (
                        <div style={{ fontSize: '11px', color: isDark ? '#fde68a' : '#78350f' }}>
                            <strong>Sugestão:</strong> Ajuste os valores para que a soma seja 100%.
                            {percentages.total < 100 && (
                                <div>Adicione {formatPercentage(100 - percentages.total)} distribuídos entre os macros.</div>
                            )}
                            {percentages.total > 100 && (
                                <div>Reduza {formatPercentage(percentages.total - 100)} da distribuição atual.</div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Ideal Distribution Suggestions */}
            {percentages.isValid && totalCalories > 0 && (
                <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: isDark ? '#064e3b' : '#f0fdf4',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: isDark ? '#d1fae5' : '#166534'
                }}>
                    <div style={{ fontWeight: 600, marginBottom: '6px' }}>✓ Distribuição Válida</div>
                    <div style={{ fontSize: '11px', color: isDark ? '#a7f3d0' : '#15803d' }}>
                        {percentages.protein >= 25 && percentages.protein <= 35 && 
                         percentages.carbs >= 40 && percentages.carbs <= 50 &&
                         percentages.fats >= 20 && percentages.fats <= 30 ? (
                            <div>Distribuição ideal para a maioria dos objetivos!</div>
                        ) : percentages.protein > 35 ? (
                            <div>💡 Alta proteína - ideal para ganho de massa muscular</div>
                        ) : percentages.carbs < 30 ? (
                            <div>💡 Low-carb - ideal para perda de peso</div>
                        ) : (
                            <div>Distribuição balanceada</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
